import { useMemo } from 'react';
import { useClasses } from '@kanda-libs/ks-design-library';
import type { StringIndexedObject } from '@kanda-libs/ks-component-ts';

import { CLASS_NAMES, VARIANTS } from './FinancePlan-constants';
import { calculateLoanCosts, getFinanceOptionDetails } from '../../utils';
import type { ViewComponentProps } from './FinancePlan-view';

interface ContainerComponentChildrenArgs {
  formattedTerm: string;
  deposit?: string | number;
  monthly: number;
  total: number;
  fee: number;
  classNames: StringIndexedObject<string>;
  isLoading?: boolean;
}

interface ContainerComponentProps extends ViewComponentProps {
  children: (args: ContainerComponentChildrenArgs) => JSX.Element;
}

const ContainerComponent = ({
  children,
  total,
  deposit,
  plan,
  className,
  variant,
  isLoading,
}: ContainerComponentProps) => {
  const VARIANT = VARIANTS[variant as string] || VARIANTS.default;

  const classNames = useClasses(CLASS_NAMES, {
    container: ['.container', className],
    wrapper: ['.wrapper', VARIANT.wrapper],
    headingMobile: ['.headingMobile', VARIANT.headingMobile],
    headingDesktop: ['.headingDesktop', VARIANT.headingDesktop],
    grid: ['.grid', VARIANT.grid],
    title: ['.title', VARIANT.title],
    pounds: ['.pounds', VARIANT.pounds],
    text: ['.text', VARIANT.text],
  });

  const costs = calculateLoanCosts(
    { totalIncVat: total },
    plan,
    deposit as number,
  );

  const { duration } = getFinanceOptionDetails(plan);

  const formattedTerm = useMemo(() => {
    if (duration < 12) return `${duration} months`;
    if (duration === 12) return `${duration / 12} year`;
    return `${duration / 12} years`;
  }, [duration]);

  if (isLoading)
    return children({
      formattedTerm: '0 months',
      deposit: 0,
      monthly: 0,
      total: 0,
      fee: 0,
      classNames,
      isLoading,
    });

  return children({
    formattedTerm,
    deposit,
    monthly: costs?.monthly || 0,
    total: costs?.total || 0,
    fee: costs?.financeCost || 0,
    classNames,
    isLoading,
  });
};

ContainerComponent.displayName = 'FinancePlan-container';

export default ContainerComponent;
